import { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Card, CardContent, Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { getImg } from 'src/services/AssetApi';
import MultiImageUploader from 'src/components/uploads/images-uploader';

import SubmitButton, { useCustomFormik } from 'src/components/formik-helper';
import QuillEditor from 'src/components/quill-editor';

import DateAndTimePicker from 'src/components/widgets/date-time-picker';
import ReadOnlyTextField from 'src/components/widgets/readonly-textfield';
import NumberTextField from 'src/components/widgets/number-textfield';
import {ocrDetection, getSecondsByString} from 'src/utils/ocr-timemark';

import { initialValues, validationSchema, handleSubmitted } from './formik';
import { MobileDateTimePicker } from '@mui/x-date-pickers/MobileDateTimePicker';
import LoadingDialog from 'src/components/dialog-loading';
import { mergeNoteFromEditor } from 'src/utils/formatters';

export const DetailForm = (props) => {
  const { item, apiUpdate, apiRemove, apiTicket, toastError, ...other } = props;
  const editorRef = useRef();

  const isRemoved = useMemo(() => {
    return item.ticket ? false : (item.checkOut > 0 || false);
  }, [item.onlyView, item.checkOut]);

  const formik = useCustomFormik({
    // initialValues: {},
    // validationSchema: validationSchema(item),

    onSubmit: async (values) => {
      const mergeValues = mergeNoteFromEditor(values, editorRef, item.note);
      await handleSubmitted(mergeValues, item, apiUpdate, apiRemove, apiTicket);
    },
  });

  useEffect(() => {
    initialValues(formik, item);
    
    if (item) {
      // Map through item.assets array and update the name for each object
      const updatedAssets =
        item.evidences?.map((img) => ({
          ...img,
          name: getImg(img.name),
          selected: img.thumb,
          readonly: true,
          disabled: !img.thumb,
        })) || [];
      // Set formik field with the updated assets array
      formik?.setFieldValue('defaultImgs', updatedAssets);
    }
  }, []);

  const [justSelected, setJustSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoFieldIn, setAutoFieldIn] = useState();
  const [autoFieldOut, setAutoFieldOut] = useState();
  const handleLocalFile = async (listSelected) => {
    const length = listSelected.length;
    if(0 === length){
      setAutoFieldIn(0);
      setAutoFieldOut(0);
    }else if(1 === length || 2 === length){
      const justSelected = listSelected[0];
      
      if(justSelected){
        setLoading(true);

        try{
          var seconds = justSelected.modified;
          if((0 === seconds) && justSelected.name){
            seconds = getSecondsByString(justSelected);
          }
          const { timestamp, location } = await ocrDetection(justSelected.file, seconds);
          justSelected.location = location;
          justSelected.timestamp = timestamp;
          setJustSelected(justSelected);

          if(0 < timestamp){  
            if(1 === length){
              setAutoFieldIn(timestamp);
              setAutoFieldOut(0);
            }else{
              setAutoFieldOut(timestamp);
            }
          }
        }catch(error){
          toastError(error);
        }finally{
          setLoading(false);
        }
      }
    }
  };

  return (
    <>
    <form onSubmit={formik.handleSubmit} {...other}>
      <Card>
        <CardContent sx={{ px: 1, py: 2 }}>
          <Grid container spacing={3} alignItems="flex-start">
            <Grid xs={12} md={6} container spacing={3}>
              {item.ticket && (
                <Grid xs={12} md={12} display="flex" justifyContent="center">
                  <Typography
                    color="error.main"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      textAlign: 'center',
                    }}>
                    {item.ticket.reason} 
                  </Typography>
                </Grid>
              )}
              
              <Grid xs={6} md={6} sm={6}>
                <ReadOnlyTextField label="Ngày nhận xe" value={item.checkinSys} />
              </Grid>

              <Grid xs={6} md={6} sm={6}>
                <ReadOnlyTextField label="Ngày trả xe" value={item.checkoutSys} />
              </Grid>

              <Grid xs={12}>
                <ReadOnlyTextField label="Biển số" value={item.car?.name} />
              </Grid>

              <Grid xs={6} md={6} sm={6}>
                <NumberTextField
                  label="Km lúc nhận"
                  name="checkinKm"
                  formik={formik}
                  inputProps={{ maxLength: 7 }}
                  hintValue={item.checkinKm}
                />
              </Grid>

              <Grid xs={6} md={6} sm={6}>
                <NumberTextField
                  label="Km lúc trả"
                  name="checkoutKm"
                  formik={formik}
                  inputProps={{ maxLength: 7 }}
                  hintValue={item.checkoutKm}
                />
              </Grid>

              <Grid xs={12} md={6} sx={{ display: 'none'}}>
                <DateAndTimePicker
                  label="Giờ xe ra"
                  name="checkinBy"
                  format="dd/MM/yyyy - HH:mm"
                  formik={formik}
                  hintValue={item.checkinBy}
                >
                  <MobileDateTimePicker ampm={false} />
                </DateAndTimePicker>
              </Grid>

              <Grid xs={12} md={6} sx={{ display: 'none'}}>
                <DateAndTimePicker
                  label="Giờ xe về"
                  name="checkoutBy"
                  format="dd/MM/yyyy - HH:mm"
                  formik={formik}
                  hintValue={item.checkoutBy}
                >
                  <MobileDateTimePicker ampm={false} />
                </DateAndTimePicker>
              </Grid>

              <Grid xs={12}>
                <QuillEditor
                  ref={editorRef}
                  value={item.note}
                />
              </Grid>
              {/* Web Browser */}
              <Grid xs={12}
                sx={{
                  display: { xs: 'none', md: 'flex' },
                  justifyContent: { xs: 'center', md: 'flex-end' }
                }}>
                <SubmitButton formik={formik} isRemove={isRemoved} />
              </Grid>
            </Grid>
            
            <Grid xs={12} md={6}>
              <MultiImageUploader formik={formik} maxFiles={7} useSelect={true} maxSelect={2}
                  onImageSelected={(image) => {handleLocalFile(image);}} onImageRefresh={justSelected}/>
            </Grid>
            {/* Mobile */}
            <Grid xs={12} md={6} 
              sx={{
                display: { xs: 'flex', md: 'none' },
                justifyContent: 'center'
              }}
            >
              <SubmitButton formik={formik} isRemove={isRemoved} />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </form>
    <LoadingDialog open={loading} message={'Đang đọc hình'}/>
    </>
  );
};

DetailForm.propTypes = {
  item: PropTypes.object.isRequired,
  apiUpdate: PropTypes.func,
  apiRemove: PropTypes.func,
  toastError: PropTypes.func,
};
